import type { StudentTrialAccessSummary } from "~/types/profile";

export interface StudentLearningAccess {
  isBlocked: boolean;
  canAccessLearning?: boolean;
}

export interface StudentAccessNavigation {
  isBlocked: boolean;
  canAccessLearning?: boolean;
  allowedSections: string[];
  hasSchool: boolean;
}

/** The server's capability takes precedence over the administrative block. */
export function canStudentAccessLearning(
  access: StudentLearningAccess,
): boolean {
  return access.canAccessLearning ?? !access.isBlocked;
}

/** Local time only schedules a profile request; it never grants access. */
export function nextStudentAccessBoundary(
  trial: {
    current: StudentTrialAccessSummary | null;
    next: StudentTrialAccessSummary | null;
  },
  now = Date.now(),
): number | null {
  const boundaries = [trial.current?.ends_at, trial.next?.starts_at]
    .map((value) => (value ? Date.parse(value) : Number.NaN))
    .filter((value) => Number.isFinite(value) && value > now);
  return boundaries.length ? Math.min(...boundaries) : null;
}

const studentRouteSections: Array<{ prefix: string; section: string }> = [
  { prefix: "/student-dashboard/requests", section: "requests" },
  { prefix: "/student-dashboard/agreements", section: "agreements" },
  { prefix: "/student-dashboard/payments", section: "payments" },
  { prefix: "/student-dashboard/account", section: "account_security" },
  { prefix: "/student-dashboard/contact", section: "school_contact" },
];

function matchesPath(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}/`);
}

export function studentDashboardRedirect(
  path: string,
  access: StudentAccessNavigation,
): string | null {
  if (matchesPath(path, "/student-dashboard/agreements") && !access.hasSchool) {
    return "/student-dashboard/requests";
  }
  if (canStudentAccessLearning(access)) return null;
  const section = studentRouteSections.find((item) =>
    matchesPath(path, item.prefix),
  )?.section;
  return section && access.allowedSections.includes(section)
    ? null
    : "/student-dashboard/requests";
}

export function studentLearningAccessRedirect(
  path: string,
  access: StudentAccessNavigation,
): string | null {
  const pathname = path.split(/[?#]/)[0];
  if (matchesPath(pathname, "/student-dashboard"))
    return studentDashboardRedirect(pathname, access);
  if (
    !canStudentAccessLearning(access) &&
    ["/lessons", "/teaching-classes", "/video-call"].some((prefix) =>
      matchesPath(pathname, prefix),
    )
  )
    return "/student-dashboard/requests";
  return null;
}
